"use client";

import { useEffect, useState } from "react";
import Script from "next/script";
import { sha1 } from "js-sha1";
import { sha256 } from "js-sha256";
import { sendWebEvent } from "~/lib/webevents/WebEventsClient";
import { sendGtmEvent } from "~/lib/gtm/gE";
import { useUnauthorizedEventService } from "~/services/api/services/event";
import { sendFacebookEvent } from "~/lib/facebookEvents/FacebookEventsClient";
import { getStorageItem, setStorageItem } from "~/lib/storage/Storage";
import { STORAGE_KEYS } from "~/types/Storage";
import { APP_ROUTES } from "~/constants/appRoutes";
import { useUpdateKlaviyoUserProfile } from "~/lib/klaviyo/hooks/useUpdateKlaviyoUserProfile";
import { AppConfig } from "~/utils/AppConfig";

const KLAVIYO_INTERVAL = 1000;
const KLAVIYO_TIMEOUT = 6000;
const KLAVIYO_SCRIPT_URL = "https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=VQw3Yi";

const KLAVIYO_FORM_IDS = {
    email: "YaPgSe",
};

const EXCLUDE_URLS = [
    "/p/vsl-hormone-care-a",
    "/p/vsl-hormone-care-b",
    APP_ROUTES.HOME,
    APP_ROUTES.SIGN_IN,
    "/p/influencer",
    APP_ROUTES.THANK_YOU,
    APP_ROUTES.THANKS_FOR_ID_UPLOAD
];

export const showForm = ({ value = "YaPgSe" }) => {
    if (AppConfig.isProduction) {
        return;
    }
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(["openForm", value]);
};

const KlaviyoScript = () => {
    const [render, setRender] = useState(false);

    const sendBigQueryEvent = useUnauthorizedEventService();
    const updateKlaviyoUser = useUpdateKlaviyoUserProfile();

    useEffect(() => {
        const handleForms = (e: any) => {
            if (e.detail.formId) {
                let klaviyoFormIds = getStorageItem(STORAGE_KEYS.KLAVIYO_FORM_IDS);
                if (klaviyoFormIds) {
                    klaviyoFormIds += ",";
                } else {
                    klaviyoFormIds = "";
                }

                klaviyoFormIds += e.detail.formId;
                setStorageItem(STORAGE_KEYS.KLAVIYO_FORM_IDS, klaviyoFormIds);

                if (e.detail.formId === KLAVIYO_FORM_IDS.email) {
                    if (e.detail.type === "open") {
                        sendGtmEvent({
                            event: "user_non_interaction",
                            value: {
                                event_name: "email_pop_up",
                                widget: "pop_up",
                                meta_data: {},
                            },
                        });

                        sendWebEvent(sendBigQueryEvent, {
                            event_name: "email_pop_up",
                            event_type: "user_non_interaction",
                            widget: "pop_up",
                            meta_data: {},
                        });
                    }

                    if (e.detail.type === "close") {
                        sendGtmEvent({
                            event: "user_interaction",
                            value: {
                                event_name: "close",
                                widget: "pop_up",
                                meta_data: {
                                    click_text: "x"
                                },
                            },
                        });

                        sendWebEvent(sendBigQueryEvent, {
                            event_name: "close",
                            event_type: "user_interaction",
                            widget: "pop_up",
                            meta_data: {
                                click_text: "x"
                            },
                        });
                    }
                }
            }

            if (e.detail.type == "submit") {
                const email = e.detail.metaData.$email;
                const widget = e.detail.formId === KLAVIYO_FORM_IDS.email ? "pop_up" : "lp_stay_in_touch";
                const interestedProduct = getStorageItem(STORAGE_KEYS.INTERESTED_PRODUCT);

                sendGtmEvent({
                    event: "user_interaction",
                    value: {
                        event_name: "created_lead_email",
                        widget,
                        meta_data: {
                            "user_email": email,
                            "additional_info": {
                                "user_email_hashed_sha1": sha1(email),
                                "user_email_hashed_sha256": sha256(email)
                            }
                        },
                    },
                });

                sendWebEvent(sendBigQueryEvent, {
                    event_name: "created_lead_email",
                    event_type: "user_interaction",
                    widget,
                    meta_data: {
                        "user_email": email,
                        "additional_info": {
                            "user_email_hashed_sha1": sha1(email),
                            "user_email_hashed_sha256": sha256(email)
                        }
                    },
                });
        
                sendFacebookEvent(sendBigQueryEvent, {
                    event_name: "submit",
                    email: email,
                });

                updateKlaviyoUser(email, {
                    most_interested_product: interestedProduct || "unknown",
                });
            }
        };

        window.addEventListener("klaviyoForms", handleForms);
        return () => window.removeEventListener("klaviyoForms", handleForms);
    }, [sendBigQueryEvent, updateKlaviyoUser]);

    useEffect(() => {
        const myListener = () => {
            document.removeEventListener("pointermove", myListener, false);
            setRender(true);

            setTimeout(() => {
                const interval = setInterval(function () {
                    const allowed = !EXCLUDE_URLS.includes(window.location.pathname) && !window.location.pathname.includes("/pqp") && !window.location.pathname.includes("/checkout") && !window.location.pathname.includes("/quiz");
                    if (allowed) {
                        clearInterval(interval);
                        const klaviyoFormIds = getStorageItem(STORAGE_KEYS.KLAVIYO_FORM_IDS) || "";
                        if (!klaviyoFormIds.includes(KLAVIYO_FORM_IDS.email)) {
                            showForm({ value: KLAVIYO_FORM_IDS.email });
                        }
                    }
                }, KLAVIYO_INTERVAL);
            }, KLAVIYO_TIMEOUT);
        };

        document.addEventListener("pointermove", myListener, false);
    }, []);

    if (!render) {
        return null;
    }

    return <Script src={KLAVIYO_SCRIPT_URL} crossOrigin="anonymous" />
};

export default KlaviyoScript;
